/**
 * StationHealthCheck Component
 * Shows a diagnostic modal for a single station with real-time health checks.
 */
import React, { useState, useEffect } from 'react';
import {
  Wifi, WifiOff, Music, Mic, Fingerprint, Clock, Power,
  CheckCircle2, XCircle, AlertTriangle, Loader2, X,
  Radio, Database, Settings2, Stethoscope, Activity, ListChecks
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import MiniDiagnosticExecutiveSummary from './MiniDiagnosticExecutiveSummary';

const StatusBadge = ({ ok, warning, label }) => {
  if (ok === null || ok === undefined) {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"><Loader2 className="w-3 h-3 animate-spin" />{label || 'Loading'}</span>;
  }
  if (warning) {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"><AlertTriangle className="w-3 h-3" />{label || 'Warning'}</span>;
  }
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"><CheckCircle2 className="w-3 h-3" />{label || 'OK'}</span>
    : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"><XCircle className="w-3 h-3" />{label || 'Error'}</span>;
};

const Section = ({ icon: Icon, title, children, status }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
        <Icon className="w-4 h-4" />
        {title}
      </div>
      {status}
    </div>
    <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
      {children}
    </div>
  </div>
);

const Row = ({ label, value, ok, mono }) => (
  <div className="flex items-center justify-between py-0.5">
    <span className="text-gray-500 dark:text-gray-500">{label}</span>
    <span className={`font-medium ${mono ? 'font-mono' : ''} ${
      ok === true ? 'text-green-600 dark:text-green-400' :
      ok === false ? 'text-red-600 dark:text-red-400' :
      'text-gray-800 dark:text-gray-200'
    }`}>
      {value}
    </span>
  </div>
);

const StationHealthCheck = ({ stationId, stationName, open, onClose, apiUrl, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && stationId) {
      fetchDiagnostic();
    }
  }, [open, stationId]);

  const fetchDiagnostic = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const resp = await fetch(`${apiUrl}/api/stream-monitor/diagnostic/station-health/${stationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const checks = data?.checks || {};
  const problems = data?.problems_found || [];
  const healthStatus = data?.health_status;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="station-health-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="w-5 h-5 text-blue-500" />
            <span>Diagnostic: {stationName}</span>
            {healthStatus && (
              <StatusBadge
                ok={healthStatus === 'HEALTHY'}
                warning={healthStatus === 'WARNING'}
                label={healthStatus}
              />
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Checking station health...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-400 text-sm">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-3">

            {/* 2026-07-17 — Executive Summary (12 sections, top of modal) */}
            {data.executive_summary && (
              <MiniDiagnosticExecutiveSummary es={data.executive_summary} />
            )}

            {/* Problems Summary */}
            {problems.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  {problems.length} {problems.length === 1 ? 'Problem' : 'Problems'} Found
                </div>
                {problems.map((p, i) => (
                  <div key={i} className="text-xs text-red-600 dark:text-red-400 py-0.5 flex items-start gap-1.5">
                    <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {p}
                  </div>
                ))}
              </div>
            )}

            {problems.length === 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                No problems detected
              </div>
            )}

            {/* 1. Stream URL — enriched status */}
            {(() => {
              const streamStatus = checks.stream_status || {};
              const sev = streamStatus.severity;
              const badge = sev === 'ok'
                ? <StatusBadge ok={true} label={streamStatus.label || 'Online'} />
                : sev === 'critical'
                  ? <StatusBadge ok={false} label={streamStatus.label || 'Offline'} />
                  : sev === 'warning'
                    ? <StatusBadge warning={true} label={streamStatus.label || 'Warning'} />
                    : <StatusBadge ok={checks.stream_url?.accessible} label={checks.stream_url?.accessible ? 'Online' : 'Offline'} />;
              return (
                <Section
                  icon={Wifi}
                  title="Stream URL"
                  status={badge}
                >
                  <Row label="URL" value={data.checks?.stream_url ? (checks.stream_url?.accessible ? 'Accessible' : checks.stream_url?.error || 'Unreachable') : '-'} ok={checks.stream_url?.accessible} />
                  <Row label="HTTP Status" value={checks.stream_url?.status || '-'} ok={checks.stream_url?.status === 200} />
                  <Row label="ICY Support" value={checks.stream_url?.has_icy ? 'Yes' : 'No'} ok={checks.stream_url?.has_icy} />
                  {streamStatus.detail && (
                    <div className={`text-[11px] mt-1 ${
                      sev === 'ok' ? 'text-green-600 dark:text-green-400' :
                      sev === 'critical' ? 'text-red-600 dark:text-red-400' :
                      'text-amber-600 dark:text-amber-400'
                    }`} data-testid="stream-status-detail">
                      {streamStatus.detail}
                    </div>
                  )}
                </Section>
              );
            })()}

            {/* 2. ICY Metadata — enriched (protocol vs song separation) */}
            {(() => {
              const ims = checks.icy_metadata_status || {};
              const legacy = checks.icy_metadata || {};
              const protoSupported = ims.protocol_supported;
              const hasTitle = !ims.current_stream_title_is_empty && !legacy.looks_like_show_name;
              return (
                <Section
                  icon={Music}
                  title="ICY Metadata"
                  status={<StatusBadge
                    ok={hasTitle}
                    warning={protoSupported && !hasTitle}
                    label={
                      !protoSupported ? 'No Protocol' :
                      hasTitle ? 'Song Found' :
                      ims.current_stream_title_looks_like_show_name ? 'Show Name' :
                      'Empty'
                    }
                  />}
                >
                  <Row label="ICY Protocol Supported" value={ims.protocol_supported_label || 'NO'} ok={protoSupported} />
                  <Row label="Current StreamTitle" value={ims.current_stream_title || 'EMPTY'} mono ok={hasTitle} />
                  {ims.reason && (
                    <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                      Reason: {ims.reason}
                    </div>
                  )}
                  {ims.recognition_continues_using && (
                    <div className="text-[11px] text-blue-600 dark:text-blue-400 mt-1">
                      Recognition continues using: <span className="font-semibold">{ims.recognition_continues_using}</span>
                    </div>
                  )}
                </Section>
              );
            })()}

            {/* Recording Statistics (schedule-aware) */}
            {checks.recording_statistics && (
              <Section
                icon={Clock}
                title="Today's Recordings"
                status={<StatusBadge
                  ok={typeof checks.recording_statistics.coverage_pct === 'number' && checks.recording_statistics.coverage_pct >= 60}
                  warning={typeof checks.recording_statistics.coverage_pct === 'number' && checks.recording_statistics.coverage_pct < 60}
                  label={typeof checks.recording_statistics.coverage_pct === 'number' ? `${checks.recording_statistics.coverage_pct}%` : '—'}
                />}
              >
                <Row
                  label="Recording Window"
                  value={
                    checks.recording_statistics.recording_window?.enabled
                      ? `${checks.recording_statistics.recording_window.start} → ${checks.recording_statistics.recording_window.end}`
                      : 'Always (no schedule)'
                  }
                />
                <Row label="Elapsed Recording Time" value={checks.recording_statistics.elapsed_recording_time_label || '—'} />
                <Row label="Songs Recorded Today" value={checks.recording_statistics.songs_recorded_today} ok={checks.recording_statistics.songs_recorded_today > 0} />
                <Row label="Estimated Expected" value={checks.recording_statistics.estimated_expected_songs} />
                <Row label="Coverage" value={typeof checks.recording_statistics.coverage_pct === 'number' ? `${checks.recording_statistics.coverage_pct}%` : 'not available'} />
              </Section>
            )}

            {/* 3. Fingerprinting */}
            <Section
              icon={Fingerprint}
              title="Audio Fingerprinting"
              status={<StatusBadge
                ok={checks.fingerprinting?.ffmpeg_installed && checks.fingerprinting?.fpcalc_installed}
                label={checks.fingerprinting?.ffmpeg_installed ? 'Ready' : 'Missing FFmpeg'}
              />}
            >
              <Row label="FFmpeg" value={checks.fingerprinting?.ffmpeg_installed ? 'Installed' : 'MISSING'} ok={checks.fingerprinting?.ffmpeg_installed} />
              <Row label="fpcalc" value={checks.fingerprinting?.fpcalc_installed ? 'Installed' : 'MISSING'} ok={checks.fingerprinting?.fpcalc_installed} />
              <Row label="Enabled" value={checks.fingerprinting?.fingerprint_enabled ? 'Yes' : 'No'} ok={checks.fingerprinting?.fingerprint_enabled} />
              <Row label="Unlimited" value={checks.fingerprinting?.fingerprint_unlimited ? 'Yes' : 'No'} />
            </Section>

            {/* 4. Metadata Sources — enriched w/ disabled reasons */}
            {(() => {
              const ms = checks.metadata_sources || {};
              const active = ms.active_sources || [];
              return (
                <Section
                  icon={Settings2}
                  title="Metadata Sources"
                  status={<StatusBadge
                    ok={active.length > 0}
                    warning={active.length === 1 && active[0] === 'icy'}
                    label={active.length ? active.map(s => s.toUpperCase()).join(', ') : 'None'}
                  />}
                >
                  {['icy', 'orb', 'mytuner'].map((key) => {
                    const block = ms[key] || {};
                    const label = key === 'mytuner' ? 'myTuner' : key.toUpperCase();
                    if (block.status === 'enabled') {
                      return (
                        <Row
                          key={key}
                          label={label}
                          value={block.id ? `Enabled (ID: ${block.id})` : 'Enabled'}
                          ok={true}
                        />
                      );
                    }
                    // disabled variant — show full reason
                    return (
                      <div key={key} className="py-0.5" data-testid={`metadata-source-${key}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-500">{label}</span>
                          <span className="font-medium text-gray-500 dark:text-gray-500">Disabled</span>
                        </div>
                        {block.reason && (
                          <div className="text-[11px] text-gray-400 dark:text-gray-500 pl-2 mt-0.5">
                            Reason: {block.reason_label || block.reason.replace(/_/g, ' ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Section>
              );
            })()}

            {/* 4b. Recognition Runtime (expanded) */}
            {(checks.recognition_runtime_expanded || checks.recognition_runtime) && (() => {
              const rt = checks.recognition_runtime_expanded || checks.recognition_runtime;
              return (
                <Section
                  icon={Mic}
                  title="Recognition Runtime"
                  status={<StatusBadge
                    ok={rt.runtime_confirmed}
                    warning={!rt.runtime_confirmed}
                    label={rt.runtime_confirmed_label || (rt.runtime_confirmed ? 'Runtime Confirmed' : 'No Recent Activity')}
                  />}
                >
                  <Row label="Recognition Strategy" value={rt.strategy_label || rt.mode_label || '—'} />
                  <Row label="Primary Method" value={rt.primary_method || '—'} />
                  <Row label="Provider" value={rt.provider || '—'} />
                  <Row label="Fallback Chain" value={(rt.fallback_chain || [rt.fallback]).join(' → ')} />
                  <Row label="Current Active Source" value={rt.current_active_source || 'not available'} />
                </Section>
              );
            })()}

            {/* 4c. Last Recognition Attempts (NEW) */}
            {checks.last_recognition_attempts && checks.last_recognition_attempts.length > 0 && (
              <Section
                icon={Activity}
                title="Last Recognition Attempts"
                status={<StatusBadge
                  ok={true}
                  label={`${checks.last_recognition_attempts.length} attempts`}
                />}
              >
                <div className="space-y-2" data-testid="last-recognition-attempts">
                  {checks.last_recognition_attempts.slice(0, 8).map((a, i) => {
                    const ts = a.timestamp && a.timestamp !== 'not_available'
                      ? new Date(a.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                      : '—';
                    const dur = a.duration_ms ? `${(a.duration_ms / 1000).toFixed(1)}s` : null;
                    const isSuccess = a.result === 'SUCCESS';
                    const isFailed = a.result === 'FAILED';
                    return (
                      <div key={i} className="border-l-2 pl-2 py-0.5" style={{
                        borderColor: isSuccess ? '#22c55e' : isFailed ? '#ef4444' : '#a3a3a3'
                      }} data-testid={`attempt-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-gray-500">{ts}</span>
                          <span className={`text-[11px] font-semibold ${
                            isSuccess ? 'text-green-600 dark:text-green-400' :
                            isFailed ? 'text-red-600 dark:text-red-400' :
                            'text-gray-500'
                          }`}>{a.result}</span>
                        </div>
                        {isSuccess && (a.artist || a.title) && (
                          <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                            {a.artist} — {a.title}
                          </div>
                        )}
                        {a.reason && !isSuccess && (
                          <div className="text-[11px] text-red-600 dark:text-red-400">
                            Reason: {a.reason}
                          </div>
                        )}
                        {dur && (
                          <div className="text-[11px] text-gray-400">Duration: {dur}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* 4d. Recent Recording Decisions (NEW) */}
            {checks.recent_recording_decisions && checks.recent_recording_decisions.length > 0 && (
              <Section
                icon={ListChecks}
                title="Recent Recording Decisions"
                status={<StatusBadge
                  ok={true}
                  label={`${checks.recent_recording_decisions.length} decisions`}
                />}
              >
                <div className="space-y-2" data-testid="recent-recording-decisions">
                  {checks.recent_recording_decisions.slice(0, 8).map((d, i) => {
                    const ts = d.timestamp && d.timestamp !== 'not_available'
                      ? new Date(d.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                      : '—';
                    const dec = d.decision || 'UNKNOWN';
                    const color = dec === 'SAVED' ? 'text-green-600 dark:text-green-400' :
                      dec === 'MERGED' ? 'text-blue-600 dark:text-blue-400' :
                      dec === 'SKIPPED' ? 'text-amber-600 dark:text-amber-400' :
                      dec === 'REJECTED' ? 'text-orange-600 dark:text-orange-400' :
                      dec === 'FAILED' ? 'text-red-600 dark:text-red-400' :
                      'text-gray-500';
                    return (
                      <div key={i} className="border-l-2 pl-2 py-1" style={{
                        borderColor: dec === 'SAVED' ? '#22c55e' :
                          dec === 'MERGED' ? '#3b82f6' :
                          dec === 'SKIPPED' ? '#f59e0b' :
                          dec === 'REJECTED' ? '#f97316' :
                          dec === 'FAILED' ? '#ef4444' : '#a3a3a3'
                      }} data-testid={`decision-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-gray-500">{ts}</span>
                          <span className={`text-[11px] font-semibold ${color}`}>{dec}</span>
                        </div>
                        {(d.artist || d.title) && dec !== 'FAILED' && (
                          <div className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                            {d.artist} — {d.title}
                          </div>
                        )}
                        {d.reason && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-500">
                            {d.stage && <span className="font-semibold">{d.stage}: </span>}
                            {d.reason}
                          </div>
                        )}
                        {/* Expanded forensic fields */}
                        {(d.session_id || d.transitions?.length > 0 || d.confidence_max || d.observations_count) && (
                          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 space-y-0.5">
                            {d.session_id && <div>Session: <span className="font-mono">{String(d.session_id).slice(0, 12)}…</span></div>}
                            {d.transitions?.length > 0 && (
                              <div>Transitions: <span className="font-mono">{d.transitions.join(' → ')}</span></div>
                            )}
                            {typeof d.confidence_max === 'number' && (
                              <div>Confidence: {d.confidence_max} · Observations: {d.observations_count ?? '—'}</div>
                            )}
                            {(d.measured_duration_s || d.known_duration_s) && (
                              <div>Duration: measured {d.measured_duration_s ?? '—'}s / known {d.known_duration_s ?? '—'}s</div>
                            )}
                            {d.recognition_source && (
                              <div>Source: <span className="font-mono">{d.recognition_source}</span></div>
                            )}
                            {d.persistence_decision && d.persistence_decision !== 'MIXED' && (
                              <div>Legacy vs Session: <span className="italic">{d.persistence_decision}</span></div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* 4e. Duplicate Rejected Candidates (NEW) */}
            {checks.duplicate_sessions?.duplicates?.length > 0 && (
              <Section
                icon={AlertTriangle}
                title="Duplicate Rejected Candidates"
                status={<StatusBadge
                  warning={true}
                  label={`${checks.duplicate_sessions.duplicate_songs_count} songs`}
                />}
              >
                <div className="space-y-2" data-testid="duplicate-sessions">
                  {checks.duplicate_sessions.duplicates.slice(0, 5).map((dup, i) => (
                    <div key={i} className="border-l-2 border-amber-400 pl-2 py-1" data-testid={`duplicate-${i}`}>
                      <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 truncate">
                        {dup.artist} — {dup.title}
                      </div>
                      <div className="text-[11px] text-amber-600 dark:text-amber-400">
                        Repeated Candidate: <span className="font-semibold">YES</span> · {dup.attempts_count} attempts
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5 space-y-0.5">
                        {dup.timeline.slice(0, 4).map((t, j) => {
                          const ts = t.timestamp && t.timestamp !== 'not_available'
                            ? new Date(t.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            : '—';
                          return (
                            <div key={j} className="font-mono">
                              #{j + 1} {ts} · via {t.closed_via || t.reason_code || '?'} · {t.reason_text || t.reason_code || '—'}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* 5. Schedule & Status */}
            <Section
              icon={Clock}
              title="Schedule & Status"
              status={<StatusBadge
                ok={checks.schedule?.in_schedule}
                warning={!checks.schedule?.in_schedule && checks.schedule?.active}
                label={checks.schedule?.in_schedule ? 'In Schedule' : 'Outside Schedule'}
              />}
            >
              <Row label="Greece Time" value={data.greece_time || '-'} mono />
              <Row label="Schedule" value={`${checks.schedule?.start_time || '-'} - ${checks.schedule?.end_time || '-'}`} mono />
              <Row label="In Schedule" value={checks.schedule?.in_schedule ? 'Yes' : 'No'} ok={checks.schedule?.in_schedule} />
              <Row label="Active" value={checks.schedule?.active ? 'Yes' : 'No'} ok={checks.schedule?.active} />
              <Row label="Manual Override" value={checks.schedule?.manual_override ? 'Yes' : 'No'} />
            </Section>

            {/* 6. Recordings */}
            <Section
              icon={Database}
              title="Recordings"
              status={<StatusBadge
                ok={checks.recordings?.songs_last_24h > 0}
                warning={checks.recordings?.songs_last_24h > 0 && checks.recordings?.songs_last_1h === 0}
                label={`${checks.recordings?.songs_last_24h || 0} / 24h`}
              />}
            >
              <Row label="Last 1 hour" value={checks.recordings?.songs_last_1h ?? 0} ok={checks.recordings?.songs_last_1h > 0} />
              <Row label="Last 24 hours" value={checks.recordings?.songs_last_24h ?? 0} ok={checks.recordings?.songs_last_24h > 0} />
              <Row label="Last song" value={checks.recordings?.last_song || 'Never'} ok={checks.recordings?.last_song !== 'NEVER RECORDED'} />
            </Section>

            {/* Deploy Summary */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1">
                <Radio className="w-4 h-4" />
                Deploy Summary
              </div>
              <div className="text-xs space-y-0.5 text-blue-600 dark:text-blue-400">
                {checks.fingerprinting?.ffmpeg_installed
                  ? <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> FFmpeg ready for fingerprinting</div>
                  : <div className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> FFmpeg MISSING - fingerprinting disabled</div>
                }
                {checks.stream_url?.accessible
                  ? <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Stream URL accessible</div>
                  : <div className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> Stream URL unreachable</div>
                }
                {checks.recordings?.songs_last_24h > 0
                  ? <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Recording: {checks.recordings.songs_last_24h} songs in 24h</div>
                  : <div className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> No recordings in 24 hours</div>
                }
              </div>
            </div>

            {/* Refresh + Copy JSON */}
            <div className="flex items-center gap-2">
              <button
                onClick={fetchDiagnostic}
                disabled={loading}
                className="flex-1 py-2 text-xs font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-300 flex items-center justify-center gap-1.5"
                data-testid="health-check-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
                {loading ? 'Checking...' : 'Refresh Diagnostic'}
              </button>
              <button
                onClick={async () => {
                  try {
                    const txt = JSON.stringify(data, null, 2);
                    await navigator.clipboard.writeText(txt);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1600);
                  } catch (e) {
                    setError('Copy failed: ' + e.message);
                  }
                }}
                className="flex-1 py-2 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 rounded-lg transition-colors text-blue-700 dark:text-blue-300 flex items-center justify-center gap-1.5"
                data-testid="health-check-copy-json"
              >
                {copied ? <CheckCircle2 className="w-3 h-3" /> : <ListChecks className="w-3 h-3" />}
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StationHealthCheck;
